import { useState } from "react"
import { Send } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { csrfToken } from "@/lib/orderActions"
import { ALL_TIER_VALUE } from "@/lib/post/options"
import { continueSignedIn, holdIntent, returnHere } from "@/lib/viewingRoom/enrollment"
import { VR, fill } from "@/lib/viewingRoom/wording"

// Viewing Room: "Invite" carries a writer or editor into the
// guest's Open House project (VR-13a, OpenHouse::InvitationsController). The
// server resolves the freelancer from the slug and keeps them on the draft;
// nothing is sent until the project publishes. Any other freelancer (cover
// designer, illustrator, narrator, translator) is handed off to today's
// sign-up or sign-in and that service's form (VR-14, HandoffDialog): the click
// is kept on the server (POST /join/intent, action `handoff`), which decides
// the service from the slug and names it for the screen. The way is the
// server's `invite` field (Resume::Handoff.route), from the freelancer's own
// role, never the role a search filter presents them under.

const INVITE_PATH = "/post-project/invitation"
// The posting page reads this once, so a project just started by an invite
// does not open with the restored-draft notice.
const INVITED_HASH = "#invited"

// Today's sign-up page (User::RegistrationsController#customer_registration).
const SIGN_UP_PATH = "/customer/sign_up"

const intoOpenHouse = (freelancer) => freelancer?.invite === "open_house"
export const canInvite = (freelancer) => ["open_house", "handoff"].includes(freelancer?.invite)

async function send(method, body) {
  const res = await fetch(INVITE_PATH, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
    body: body && JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok, status: res.status, data }
}

// Starts an invite from a profile, card or comparison. A browser whose
// project already waits for confirmation goes back to it (`converted`). A
// handed-off freelancer opens HandoffDialog (`handoff`) once the server keeps
// the click.
export function useInvite() {
  const [state, setState] = useState({ busy: null, failed: null, handoff: null })
  const failed = (freelancer) => setState({ busy: null, failed: freelancer.slug, handoff: null })

  const handOff = async (freelancer) => {
    try {
      const data = await holdIntent({ action: "handoff", slug: freelancer.slug, returnTo: returnHere() })
      setState({
        busy: null, failed: null,
        handoff: { slug: freelancer.slug, penName: freelancer.penName, serviceName: data.service_name },
      })
    } catch (err) {
      if (err.reason === "signed_in") return window.location.reload()
      failed(freelancer)
    }
  }

  const start = async (freelancer) => {
    setState({ busy: freelancer.slug, failed: null, handoff: null })
    if (!intoOpenHouse(freelancer)) return handOff(freelancer)
    const { ok, data } = await send("POST", { slug: freelancer.slug }).catch(() => ({ ok: false, data: {} }))
    if (ok || data.reason === "converted") return window.location.assign(`/post-project${INVITED_HASH}`)
    failed(freelancer)
  }
  const close = () => setState((current) => ({ ...current, handoff: null }))
  return { ...state, start, close }
}

// Removes the invitation the page shows, at the draft revision it holds; a
// refusal (`stale` when either is no longer the draft's) carries its reason,
// as the posting page's other draft writes do.
export async function removeInvitation(revision, slug) {
  const { ok, status, data } = await send("DELETE", { revision, slug })
  if (!ok) throw Object.assign(new Error(data.reason || "Request failed"), { status, reason: data.reason, data })
  return data
}

export function arrivedInviting() {
  if (window.location.hash !== INVITED_HASH) return false
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)
  return true
}

export function InviteButton({ freelancer, invite, className }) {
  return (
    <Button className={className} disabled={!canInvite(freelancer) || !!invite.busy} onClick={() => invite.start(freelancer)}>
      <Send className="size-4" strokeWidth={2} />
      {VR.action.invite}
    </Button>
  )
}

// The handoff for a service Open House does not cover (wording §10). Continue
// is today's sign-up, Sign in today's sign-in (through the stable continue
// address); either way the kept click lands the signed-in customer on that
// service's form. Back to profile closes it on the profile (`onProfile`) and
// opens the freelancer's profile from search.
export function HandoffDialog({ invite, onProfile = false }) {
  const handoff = invite.handoff
  if (!handoff) return null

  const words = { service_name: handoff.serviceName, pen_name: handoff.penName }
  const back = onProfile
    ? invite.close
    : () => window.location.assign(`/freelancers/${encodeURIComponent(handoff.slug)}`)

  return (
    <Dialog open onOpenChange={(open) => !open && invite.close()}>
      <DialogContent showCloseButton={false} className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{fill(VR.handoff.title, words)}</DialogTitle>
          <DialogDescription>{fill(VR.handoff.body, words)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:justify-start">
          <Button asChild><a href={SIGN_UP_PATH}>{VR.handoff.continue}</a></Button>
          <Button variant="outline" onClick={continueSignedIn}>{VR.handoff.signIn}</Button>
          <Button variant="ghost" onClick={back}>{VR.handoff.back}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// The failed invite's line: for this freelancer, or for any when none is named.
export const InviteFailed = ({ invite, freelancer }) =>
  invite.failed && (!freelancer || invite.failed === freelancer.slug)
    ? <p role="alert" className="text-[12.5px] text-destructive">{VR.error.generic}</p>
    : null

// What the writer level says about the invited writer: open to every level,
// a level that leaves them out, the level prefilled for them, or another
// level they can request.
function levelState(invitation, tier, restored) {
  if (invitation.role !== "writer" || !tier) return null
  if (tier === ALL_TIER_VALUE) return "included"
  if (!invitation.includedLevels?.includes(tier)) return "excluded"
  return tier === invitation.level && !restored ? "prefilled" : "restored"
}

// The invitation strip on the Open House preparation screens (wording §8).
// `onTier` changes the writer level as the level step does, so the price
// follows it; `onRemove` removes the invitation through the page's draft
// writes and settles once the server has, or throws its refusal.
export function InvitationStrip({ invitation, tier, onTier, onRemove }) {
  const [restored, setRestored] = useState(false)
  const [removed, setRemoved] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [failed, setFailed] = useState(false)

  if (!invitation) {
    return removed ? <p role="status" className="rounded-xl border border-border bg-accent px-4 py-3 text-[13px]">{VR.invite.removed}</p> : null
  }

  const words = { pen_name: invitation.penName }
  const level = levelState(invitation, tier, restored)
  // A stale draft is the page's own newer-version notice to show.
  const remove = async () => {
    setRemoving(true)
    setFailed(false)
    try {
      await onRemove(invitation.slug)
      setRemoved(true)
    } catch (err) {
      if (err?.reason !== "stale") setFailed(true)
    } finally {
      setRemoving(false)
    }
  }
  const restore = () => {
    setRestored(true)
    onTier(invitation.level)
  }

  return (
    <section aria-label={fill(VR.invite.strip, words)} className="flex flex-col gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5 text-[13px]">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-0 flex-[1_1_16rem]">
          <p className="font-display text-[14px] font-bold text-foreground">{fill(VR.invite.strip, words)}</p>
          {invitation.role === "editor" && <p className="mt-0.5 font-semibold text-foreground">{fill(VR.invite.editorSelected, words)}</p>}
          <p className="mt-1 text-muted-foreground">{fill(VR.invite.pending, words)}</p>
          <p className="mt-1 text-muted-foreground">{VR.invite.otherFreelancers} {VR.invite.notAssigned}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 font-semibold">
          <a href={`/freelancers/${encodeURIComponent(invitation.slug)}`} className="text-foreground underline underline-offset-2">
            {VR.invite.viewProfile}
          </a>
          <button type="button" onClick={remove} disabled={removing} className="text-foreground underline underline-offset-2 disabled:opacity-50">
            {VR.invite.remove}
          </button>
        </div>
      </div>
      {failed && <p role="alert" className="text-[12.5px] text-destructive">{VR.error.generic}</p>}
      {level === "excluded" && (
        <div role="alert" className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-foreground">
          <p>{fill(VR.invite.levelExcluded, words)}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={restore}>{fill(VR.invite.restoreLevel, { invited_writer_level: invitation.level })}</Button>
            <Button size="sm" variant="outline" onClick={remove} disabled={removing}>{fill(VR.invite.continueWithout, words)}</Button>
          </div>
        </div>
      )}
      {level && level !== "excluded" && (
        <p role="status" className="rounded-lg bg-accent px-3 py-2 text-foreground">
          {level === "prefilled" && fill(VR.invite.levelPrefilled, { ...words, writer_level: invitation.level })}
          {level === "restored" && fill(VR.invite.levelRestored, words)}
          {level === "included" && fill(VR.invite.openLevelsIncluded, words)}
        </p>
      )}
    </section>
  )
}
